/**
 * Menus API Client
 * Frontend API calls for managing menus
 */

import { apiCall } from '../api';
import type {
  Menu,
  MenuResponse,
  MenusResponse,
  CreateMenuRequest,
  UpdateMenuRequest,
} from '@/types/api';

/**
 * Fetch menus with optional date range filtering
 * @param startDate - Start date (YYYY-MM-DD)
 * @param endDate - End date (YYYY-MM-DD)
 * @param status - Filter by status (admin only)
 * @returns List of menus
 */
export async function fetchMenus(
  startDate?: string,
  endDate?: string,
  status?: 'DRAFT' | 'PUBLISHED' | 'ARCHIVED'
): Promise<Menu[]> {
  const params = new URLSearchParams();

  if (startDate) {
    params.append('startDate', startDate);
  }
  if (endDate) {
    params.append('endDate', endDate);
  }
  if (status) {
    params.append('status', status);
  }

  const url = params.toString()
    ? `/api/menus?${params.toString()}`
    : '/api/menus';

  const response = await apiCall<MenusResponse>(url, {
    method: 'GET',
  });

  return response.menus;
}

/**
 * Fetch a single menu by ID
 * @param id - Menu ID
 * @returns Menu details
 */
export async function fetchMenuById(id: number): Promise<Menu> {
  const response = await apiCall<MenuResponse>(`/api/menus/${id}`, {
    method: 'GET',
  });
  return response.menu;
}

/**
 * Create a new menu (admin only)
 * @param data - Menu data
 * @returns Created menu
 */
export async function createMenu(data: CreateMenuRequest): Promise<Menu> {
  const response = await apiCall<MenuResponse>('/api/menus', {
    method: 'POST',
    body: JSON.stringify(data),
  });
  return response.menu;
}

/**
 * Update an existing menu (admin only)
 * @param id - Menu ID
 * @param data - Menu data to update
 * @returns Updated menu
 */
export async function updateMenu(
  id: number,
  data: UpdateMenuRequest
): Promise<Menu> {
  const response = await apiCall<MenuResponse>(`/api/menus/${id}`, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
  return response.menu;
}

/**
 * Delete a menu (admin only)
 * @param id - Menu ID
 * @throws Error if menu has RSVPs
 */
export async function deleteMenu(id: number): Promise<void> {
  await apiCall<{ message: string }>(`/api/menus/${id}`, {
    method: 'DELETE',
  });
}

/**
 * Publish a menu (admin only)
 * @param id - Menu ID
 * @returns Updated menu with PUBLISHED status
 */
export async function publishMenu(
  id: number
): Promise<{ menu: Menu; autoRsvpCount: number; autoRsvpFailed: boolean }> {
  const response = await apiCall<MenuResponse>(`/api/menus/${id}/publish`, {
    method: 'POST',
  });
  return {
    menu: response.menu,
    autoRsvpCount: response.autoRsvpCount ?? 0,
    autoRsvpFailed: response.autoRsvpFailed ?? false,
  };
}

/**
 * Unpublish a menu (admin only)
 * @param id - Menu ID
 * @returns Updated menu with DRAFT status
 * @throws Error if menu has RSVPs
 */
export async function unpublishMenu(id: number): Promise<Menu> {
  const response = await apiCall<MenuResponse>(`/api/menus/${id}/unpublish`, {
    method: 'POST',
  });
  return response.menu;
}
